<script lang="ts">
  /**
   * SwimSwiper — wraps SwimHeroCard in a horizontal-swipe surface so the user
   * can flick left/right to step through swims. Gesture mechanics are modelled
   * exactly on lib/ReviewCard.svelte and lib/actions/tapGuard.ts:
   *   - RAW addEventListener in onMount (NOT Svelte delegated onpointer* attrs)
   *   - touch-action: pan-y so the browser keeps vertical scroll, JS owns horizontal
   *   - lockAxis / TAP_SLOP / SWIPE_THRESHOLD from swipeDeckLogic (single source)
   *
   * Direction map (workouts array is most-recent-first):
   *   drag RIGHT (dx > 0) → show older swim (canPrev → nav.prev)
   *   drag LEFT  (dx < 0) → show newer swim (canNext → nav.next)
   */
  import { onMount } from "svelte";
  import SwimHeroCard from "./SwimHeroCard.svelte";
  import { workouts } from "../lib/workoutsStore";
  import { selectedWorkoutUUID } from "../lib/state";
  import { swimNavState } from "../lib/swimNav";
  import { haptic } from "../lib/haptics";
  import { formatRowDate } from "../lib/format";
  import { lockAxis, SWIPE_THRESHOLD, TAP_SLOP } from "../lib/swipeDeckLogic";
  import type { GestureAxis } from "../lib/swipeDeckLogic";

  let nav = $derived(swimNavState($workouts, $selectedWorkoutUUID));
  let i = $derived(
    $selectedWorkoutUUID === null
      ? 0
      : Math.max(0, $workouts.findIndex((w) => w.uuid === $selectedWorkoutUUID)),
  );
  // prevLabel: the older swim's date (index i+1 in most-recent-first array).
  // nextLabel: the newer swim's date (index i-1).
  let prevLabel = $derived(
    nav.canPrev && $workouts[i + 1] ? formatRowDate($workouts[i + 1].startDate) : "",
  );
  let nextLabel = $derived(
    nav.canNext && $workouts[i - 1] ? formatRowDate($workouts[i - 1].startDate) : "",
  );

  let surface = $state<HTMLDivElement | null>(null);
  let dragX = $state(0);
  let dragging = $state(false);

  onMount(() => {
    const el = surface;
    if (!el) return;

    let tracking = false;
    let startX = 0;
    let startY = 0;
    let axis: GestureAxis = null;

    function down(e: PointerEvent) {
      // Skip if the touch landed on an interactive descendant (taps inside hero
      // cards must still fire). Same guard pattern as tapGuard.ts.
      const target = e.target as Element | null;
      if (target && target.closest("button, a, input, select, textarea")) return;

      tracking = true;
      startX = e.clientX;
      startY = e.clientY;
      axis = null;
      dragX = 0;
    }

    function move(e: PointerEvent) {
      if (!tracking) return;

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      // Axis not yet committed — try to lock it.
      if (axis === null) {
        axis = lockAxis(dx, dy, TAP_SLOP);
        // Still ambiguous or locked vertical → let the browser scroll.
        if (axis !== "horizontal") return;
      }

      // Locked vertical → abort this drag, let the page scroll.
      if (axis === "vertical") return;

      // Horizontal-locked: update visual drag position.
      dragging = true;

      // Rubber-band: if dragging toward an edge that has no target, divide by 3.
      const noTarget = (dx > 0 && !nav.canPrev) || (dx < 0 && !nav.canNext);
      dragX = noTarget ? dx / 3 : dx;
    }

    function up(e: PointerEvent) {
      if (!tracking) return;
      tracking = false;

      const dx = e.clientX - startX;
      const committed = axis === "horizontal" && Math.abs(dx) >= SWIPE_THRESHOLD;

      if (committed) {
        if (dx > 0 && nav.canPrev) {
          haptic("selection");
          selectedWorkoutUUID.set(nav.prev);
        } else if (dx < 0 && nav.canNext) {
          haptic("selection");
          selectedWorkoutUUID.set(nav.next);
        }
      }

      // Always snap back to rest; content swaps via the store-driven firehose refetch.
      dragging = false;
      dragX = 0;
      axis = null;
    }

    function cancel() {
      tracking = false;
      dragging = false;
      dragX = 0;
      axis = null;
    }

    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", cancel);

    return () => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", cancel);
    };
  });
</script>

<div class="swim-swiper" bind:this={surface}>
  {#if dragging && dragX > TAP_SLOP && nav.canPrev}
    <span class="peek peek-left" aria-hidden="true">‹ {prevLabel}</span>
  {/if}
  {#if dragging && dragX < -TAP_SLOP && nav.canNext}
    <span class="peek peek-right" aria-hidden="true">{nextLabel} ›</span>
  {/if}
  <div
    class="swim-swiper-inner"
    class:snapping={!dragging}
    style="transform: translateX({dragX}px)"
  >
    <SwimHeroCard />
  </div>
</div>

<style>
  /* touch-action: pan-y — browser keeps VERTICAL scroll, JS owns HORIZONTAL.
     NEVER touch-action: none — that traps WKWebView and kills page scrolling. */
  .swim-swiper {
    position: relative;
    overflow: hidden;
    touch-action: pan-y;
  }

  .swim-swiper-inner {
    will-change: transform;
  }

  /* Snap-back transition fires when dragging becomes false (same pattern as
     ReviewCard's style:transition logic — toggled via a class here). */
  .swim-swiper-inner.snapping {
    transition: transform 220ms var(--ease-swim, ease-out);
  }

  .peek {
    position: absolute;
    top: 14px;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    font-size: 15px;
    font-weight: 600;
    color: var(--text-body);
    background: var(--bg-raised);
    padding: 5px 11px;
    border-radius: var(--radius-pill);
    white-space: nowrap;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
    pointer-events: none;
    user-select: none;
  }

  .peek-left {
    left: var(--space-3);
  }

  .peek-right {
    right: var(--space-3);
  }
</style>
